import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Flex, Box } from 'reflexbox/styled-components'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'

import media from 'media'
import theme from 'theme'

import { TextContainer } from '@anvilco-pkg/ui/components/Tabs/Tabs'

import VideoModal from 'components/VideoModal'
import UniversalLink from 'components/UniversalLink'
import FullWidthCarousel from 'components/Carousel/FullWidthCarousel'
import PlayButton from 'components/PlayButton'

import vouchWebp from 'components/videoPlaceholders/vouchPlaceholder.webp'
import sesoWebp from 'components/videoPlaceholders/sesoPlaceholder.webp'

import ascend from 'components/logo/logos/ascend'
import vouch from 'components/logo/logos/vouch'
import seso from 'components/logo/logos/seso'

const Slide = styled(Flex)
  .withConfig({
    shouldForwardProp: (prop) => ['backgroundColor'].indexOf(prop) === -1,
  })
  .attrs({
    flexDirection: ['column-reverse', 'column-reverse', 'row'],
    justifyContent: ['flex-end', 'flex-end', 'flex-start'],
  })`
  width: 100%;
  background-color: ${({ backgroundColor }) => backgroundColor};
  position: relative;
  padding: 25px 25px;
  height: 100%;

  ${media.desktop`
    height: 496px;
    padding: 56px 40px;
  `}

  ${media.giant`
    padding: 56px 80px;
  `}
`

const LeftSide = styled(Flex).attrs({
  flexDirection: ['column'],
  justifyContent: ['flex-start', 'flex-start', 'center'],
})`
  ${media.desktop`
    flex: 1;
  `}
`

const RightSide = styled(Flex).attrs({
  flexDirection: ['column'],
  justifyContent: ['center'],
  mb: [13, 40, 0],
})`
  ${media.desktop`
    flex: 1;
  `}
`

export const StyledCarousel = styled(FullWidthCarousel)`
  max-height: 509px;
  margin-bottom: 80px;

  .slick-track {
    display: flex !important;
  }

  .slick-slide {
    height: inherit !important;
    display: flex !important;
    justify-content: center;

    & > div {
      width: 100%;
      height: inherit !important;
      display: flex !important;
    }
  }

  ${media.tablet`
    margin-bottom: 0;
  `}

  /* attempt to change tab font size to be bigger */
  ${TextContainer} {
    font-size: 21px !important;
  }

  // very confused on why this stopped working after the switch from @anvilco-pkg/ui 1.19.0 --> 1.19.1
  @media (min-width: 1266px) {
    .slick-slide {
      /* width manipulation could go below paper width breakpoint, but it's a pain rn */
      margin-left: 5px;
      margin-right: 5px;
      width: 1156px !important;
    }
  }

  .slick-arrow {
    top: unset !important;
    bottom: -90px !important;

    ${media.tablet`
      top: 50% !important;
    `}
  }

  .slick-prev {
    ${media.tablet`
      left: 30px !important;
    `}
  }
`

const TestimonialText = styled(Box).attrs({
  fontSize: [15, 15, 21],
  mb: [22, 22, 27],
  maxWidth: ['100%', '100%', 365],
})`
  line-height: 1.5;
  font-weight: 500;
`

const TestimonialQuote = styled(TestimonialText)`
  font-style: italic;
`

const TestimonialAuthor = styled(Box).attrs({
  fontSize: [16, 16, 18],
  fontWeight: [600],
})`
  line-height: 1.36;
`

const TestimonialAuthorJob = styled(Box).attrs({
  fontSize: [15],
  mb: [12, 12, 26, 32],
})`
  line-height: 1.5;
`

const LogoContainer = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => ['isMobile'].indexOf(prop) === -1,
  })
  .attrs({
    mb: [12, 12, 25],
  })`
  display: ${({ isMobile }) => (isMobile ? 'block' : 'none')};

  ${media.desktop`
    display: ${({ isMobile }) => (isMobile ? 'none' : 'block')};
  `}

  svg {
    display: block;
    width: auto;
    height: 27px;

    ${media.tablet`
      height: 38px;
    `}

    ${media.desktop`
      height: 48px;
    `}
  }
`

const getPlaceholderAttrs = () => ({
  height: [138, 138, 234, 323],
  width: [197, 197, 333, 463],
})

const VideoAssetContainer = styled(Box).attrs(getPlaceholderAttrs)`
  position: relative;
  cursor: pointer;
`

const CoverImage = styled(GatsbyImage)`
  position: relative;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 2;
`

const StyledVideoAssetPlaceholderImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: relative;
  z-index: 1;
`

const VideoAssetInner = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;
`

const Shadow = styled(Box).withConfig({
  shouldForwardProp: (prop) => ['bgColor'].indexOf(prop) === -1,
})`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 0;
  background-color: ${({ bgColor }) => bgColor};
  top: 8px;
  left: 8px;

  ${media.tablet`
    top: 16px;
    left: 16px;
  `}

  ${media.tablet`
    top: 22px;
    left: 22px;
  `}
`

const SlideWrapper = styled(Box).attrs({
  px: ['10px', '10px', 0],
})``

const CoverContainer = styled(Box).attrs(getPlaceholderAttrs)`
  position: relative;
`

const HomePageCarousel = ({ hideTabs, testimonials }) => {
  const { blogs } = useStaticQuery(graphql`
    query AscendCaseStudyQuery {
      blogs: allSanityBlogPost(
        filter: {
          article: {
            title: {
              eq: "How Ascend automated premium financing agreements with Anvil's PDF Filling API"
            }
          }
        }
      ) {
        nodes {
          heroImage {
            asset {
              gatsbyImageData
            }
          }
        }
      }
    }
  `)
  const slides = [
    {
      logoObj: vouch,
      testimonial: {
        quote: testimonials.vouch,
        author: 'Travis Hedge',
        job: 'Co-Founder, Vouch',
      },
      button: {
        text: 'Read the case study',
        to: '/blog/customer-stories/vouch/',
      },
      backgroundColor: theme.colors.blues[5],
      video: {
        placeholder: vouchWebp,
        youtubeId: 'sq53cT7wExM',
        title: 'How Vouch digitized their insurance customer onboarding flow',
        shadow: theme.colors.blues[20],
        description:
          'Learn how a digital insurance provider for startups was able to increase their capacity to take on new clients.',
      },
    },
    {
      logoObj: seso,
      testimonial: {
        quote: testimonials.seso,
        author: 'Jordan Taylor',
        job: 'Co-founder & Head of Product, Seso',
      },
      button: {
        text: 'Read the case study',
        to: '/blog/customer-stories/seso/',
      },
      backgroundColor: theme.colors.greens[5],
      video: {
        placeholder: sesoWebp,
        youtubeId: 'KpDdM89ezNo',
        title: 'How Seso digitized the process of farmworker hiring',
        shadow: theme.colors.greens[20],
        description:
          'Learn how a labor marketplace for the agriculture industry was able to save weeks of engineering time.',
      },
    },
    {
      logoObj: ascend,
      title:
        "How Ascend automated insurance premium financing with Anvil's PDF Filling API",
      button: {
        text: 'Read the case study',
        to: '/blog/customer-stories/ascend/',
      },
      backgroundColor: theme.colors.blacks[5],
      cover: {
        image: blogs?.nodes?.[0].heroImage.asset.gatsbyImageData,
        shadow: theme.colors.blacks[20],
      },
    },
  ]

  return (
    <StyledCarousel
      tabs={hideTabs ? undefined : ['Insurance', 'HR', 'Finance']}
      withArrows
      centerMode
      autoplay
      autoplaySpeed={5000}
      dots={false}
      centeredTab
    >
      {slides.map(
        (
          {
            testimonial,
            title,
            logoObj,
            description,
            button,
            backgroundColor,
            video,
            cover,
          },
          index
        ) => (
          <SlideWrapper key={index}>
            <Slide backgroundColor={backgroundColor}>
              <LeftSide hasDescription={description}>
                <LogoContainer>{logoObj.getImage()}</LogoContainer>
                <div>
                  {title && <TestimonialText>{title}</TestimonialText>}
                  {testimonial && (
                    <>
                      <TestimonialQuote>{testimonial.quote}</TestimonialQuote>
                      <TestimonialAuthor>
                        {testimonial.author}
                      </TestimonialAuthor>
                      <TestimonialAuthorJob>
                        {testimonial.job}
                      </TestimonialAuthorJob>
                    </>
                  )}
                </div>
                <UniversalLink to={button.to}>{button.text}</UniversalLink>
              </LeftSide>
              <RightSide>
                {video && (
                  <VideoModal
                    title={video.title}
                    description={video.description}
                    videoId={video.youtubeId}
                    renderPlaceholder={({ onClick }) => {
                      return (
                        <VideoAssetContainer onClick={onClick}>
                          <VideoAssetInner>
                            <PlayButton />
                            <StyledVideoAssetPlaceholderImg
                              src={video.placeholder}
                              alt={video.title}
                            />
                            <Shadow bgColor={video.shadow} />
                          </VideoAssetInner>
                        </VideoAssetContainer>
                      )
                    }}
                  />
                )}
                {cover && (
                  <CoverContainer>
                    <CoverImage image={getImage(cover.image)} alt={title} />
                    <Shadow bgColor={cover.shadow} />
                  </CoverContainer>
                )}
              </RightSide>
              <LogoContainer isMobile>{logoObj.getImage()}</LogoContainer>
            </Slide>
          </SlideWrapper>
        )
      )}
    </StyledCarousel>
  )
}

HomePageCarousel.propTypes = {
  hideTabs: PropTypes.bool,
  testimonials: PropTypes.object,
}

HomePageCarousel.defaultProps = {
  hideTabs: false,
  testimonials: {
    vouch:
      '“Anvil plays a really important role for us in being able to translate the unique risks and needs of our client base into the models of how traditional insurance companies operate.”',
    seso: "“Anvil's platform provided us with all the tools to quickly and compliantly build new digital experiences directly into the Seso product, from webforms to document generation and e-sign.” ",
  },
}

export default HomePageCarousel
